// Hash routing, no dependency. `#themes`, `#ask`, `#transcripts` pick the view; `&r=<transcriptId>`
// opens the reader; `&t=<lineId>` opens the reader scrolled and highlighted at that line. A bare
// `#t=<lineId>` (the contract's line anchor) works too and lands on the transcripts view.
import { useEffect, useState } from 'react'
import type { LineId } from '../contract'

export type View = 'transcripts' | 'themes' | 'ask'
export interface Route { view: View; t?: LineId; r?: string }

const VIEWS: View[] = ['transcripts', 'themes', 'ask']

export function transcriptOf(lineId: LineId): string {
  return lineId.slice(0, lineId.lastIndexOf(':'))
}

export function parseHash(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('&').filter(Boolean)
  const route: Route = { view: 'transcripts' }
  for (const p of parts) {
    const [k, v] = p.includes('=') ? [p.slice(0, p.indexOf('=')), decodeURIComponent(p.slice(p.indexOf('=') + 1))] : [p, '']
    if (!v && VIEWS.includes(k as View)) route.view = k as View
    else if (k === 't' && /^.+:\d+$/.test(v)) { route.t = v as LineId; route.r = transcriptOf(v as LineId) }
    else if (k === 'r' && v && !route.r) route.r = v
  }
  return route
}

export function toHash(r: Route): string {
  let h = `#${r.view}`
  if (r.t) h += `&t=${encodeURIComponent(r.t)}`
  else if (r.r) h += `&r=${encodeURIComponent(r.r)}`
  return h
}

export function navigate(r: Route) {
  const h = toHash(r)
  if (location.hash !== h) location.hash = h
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseHash(location.hash))
  useEffect(() => {
    const on = () => setRoute(parseHash(location.hash))
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])
  return route
}
